// src/pages/invoices/InvoicePreview/InvoiceTotals.js
import { formatearMoneda, formatearMonto } from '../../../utils/currency';
import React from 'react';
import { Box, Typography, Paper } from '@mui/material';

export const InvoiceTotals = ({ invoice, theme = {} }) => {
  if (!invoice) return null;
  
  const isPrintMode = theme.printMode === true;
  
  // Asegurarnos de tener los items
  const items = invoice.items || [];
  
  // Los importes vienen de la factura guardada. Antes se recalculaban acá
  // mirando sólo si el ítem estaba exento, así que un ítem no gravado entraba a
  // la base imponible y el documento impreso mostraba un IVA distinto al que
  // había registrado el sistema. El reduce queda de respaldo para documentos
  // que todavía no traigan los subtotales separados.
  const porTipo = (tipo) => items.reduce((sum, item) => {
    const taxType = item.taxType || (item.exentoIva === true || item.taxExempt === true ? 'exento' : 'gravado');
    if (taxType !== tipo) return sum;
    const cantidad = typeof item.cantidad === 'number' ? item.cantidad : 1;
    const precio = typeof item.precioUnitario === 'number' ? item.precioUnitario : 0;
    return sum + (item.total ?? cantidad * precio);
  }, 0);

  const baseImponible = invoice.subtotalGravado ?? porTipo('gravado');
  const exento = invoice.subtotalExento ?? porTipo('exento');
  const noGravado = invoice.subtotalNoGravado ?? porTipo('no_gravado');
  const iva = invoice.tax ?? invoice.iva ?? baseImponible * 0.16;

  // Bases e IVA por alícuota. Si el documento no los trae (facturas anteriores al
  // desglose), todo lo gravado se muestra bajo la general, que era el único caso
  // posible entonces.
  const baseGeneral = invoice.baseGeneral ?? baseImponible;
  const baseReducida = invoice.baseReducida ?? 0;
  const baseAdicional = invoice.baseAdicional ?? 0;
  const ivaGeneral = invoice.ivaGeneral ?? iva;
  const ivaReducida = invoice.ivaReducida ?? 0;
  const ivaAdicional = invoice.ivaAdicional ?? 0;
  const igtf = invoice.igtf ?? 0;
  const total = invoice.total ?? (baseImponible + exento + noGravado + iva + (invoice.igtf ?? 0));
  
  // El formato vive en utils/currency: separadores venezolanos y "Bs." en vez de "VES".
  
  // Estilos
  const styles = {
    container: {
      width: '40%',
      marginLeft: 'auto',
      marginTop: '20px',
      marginBottom: '20px',
      overflow: 'hidden',
      borderRadius: '6px',
      boxShadow: isPrintMode ? 'none' : '0 2px 8px rgba(0,0,0,0.12)'
    },
    totalsBox: {
      backgroundColor: isPrintMode ? '#ffffff' : (theme.primary || '#003366'),
      padding: '0',
      color: isPrintMode ? '#000000' : '#FFFFFF',
      borderRadius: '6px',
      border: isPrintMode ? '1px solid #000000' : 'none'
    },
    row: {
      display: 'flex',
      justifyContent: 'space-between',
      padding: '12px 15px',
      alignItems: 'center'
    },
    baseImponibleRow: {
      borderBottom: isPrintMode ? '1px solid #000000' : '1px solid rgba(255,255,255,0.1)'
    },
    exentoRow: {
      borderBottom: isPrintMode ? '1px solid #000000' : '1px solid rgba(255,255,255,0.1)'
    },
    ivaRow: {
      borderBottom: isPrintMode ? '1px solid #000000' : '1px solid rgba(255,255,255,0.1)',
      backgroundColor: isPrintMode ? '#ffffff' : 'rgba(0,0,0,0.05)'
    },
    totalRow: {
      backgroundColor: isPrintMode ? '#ffffff' : 'rgba(0,0,0,0.1)',
      padding: '15px',
      borderTop: isPrintMode ? '2px solid #000000' : 'none'
    },
    label: {
      fontWeight: '500',
      fontSize: '14px'
    },
    value: {
      fontWeight: '600',
      fontSize: '14px',
      fontFamily: '"Roboto Mono", monospace'
    },
    totalLabel: {
      fontWeight: '600',
      fontSize: '16px'
    },
    totalValue: {
      fontWeight: '700',
      fontSize: '16px',
      fontFamily: '"Roboto Mono", monospace'
    }
  };
  
  return (
    <Box sx={styles.container} className="invoice-totals">
      <Paper elevation={0} sx={styles.totalsBox}>
        {/* Base Imponible */}
        <Box sx={{...styles.row, ...styles.baseImponibleRow}}>
          <Typography sx={styles.label}>Base Imponible:</Typography>
          <Typography sx={styles.value} className="base-imponible-value">
            {formatearMoneda(baseImponible, invoice.moneda)}
          </Typography>
        </Box>
        
        {/* Exento: sólo si la factura tiene algún ítem exento */}
        {exento > 0 && (
          <Box sx={{...styles.row, ...styles.exentoRow}}>
            <Typography sx={styles.label}>Exento:</Typography>
            <Typography sx={styles.value} className="exento-value">
              {formatearMoneda(exento, invoice.moneda)}
            </Typography>
          </Box>
        )}

        {/* No gravado: sólo aparece si la factura tiene algún ítem de ese tipo */}
        {noGravado > 0 && (
          <Box sx={{...styles.row, ...styles.exentoRow}}>
            <Typography sx={styles.label}>No gravado:</Typography>
            <Typography sx={styles.value} className="no-gravado-value">
              {formatearMoneda(noGravado, invoice.moneda)}
            </Typography>
          </Box>
        )}
        
        {/* IVA, desglosado por alícuota. Una sola línea de "IVA (16%)" no sirve
            cuando la factura mezcla alícuotas, y el Libro de Ventas las pide
            separadas. Cada renglón aparece sólo si tiene monto. */}
        {[
          { etiqueta: 'IVA (16%)', base: baseGeneral, monto: ivaGeneral },
          { etiqueta: 'IVA (8%)', base: baseReducida, monto: ivaReducida },
          { etiqueta: 'IVA (31%)', base: baseAdicional, monto: ivaAdicional }
        ].filter(fila => fila.base > 0).map(fila => (
          <Box key={fila.etiqueta} sx={{...styles.row, ...styles.ivaRow}}>
            <Typography sx={styles.label}>{fila.etiqueta}:</Typography>
            <Typography sx={styles.value} className="iva-value">
              {formatearMoneda(fila.monto, invoice.moneda)}
            </Typography>
          </Box>
        ))}

        {/* IGTF: va separado del IVA, nunca sumado adentro. */}
        {igtf > 0 && (
          <Box sx={{...styles.row, ...styles.ivaRow}}>
            <Typography sx={styles.label}>
              IGTF ({invoice.igtfPorcentaje || 3}%):
            </Typography>
            <Typography sx={styles.value} className="igtf-value">
              {formatearMoneda(igtf, invoice.moneda)}
            </Typography>
          </Box>
        )}
        
        {/* Total */}
        <Box sx={{...styles.row, ...styles.totalRow}}>
          <Typography sx={styles.totalLabel}>Total:</Typography>
          <Typography sx={styles.totalValue} className="total-value">
            {formatearMoneda(total, invoice.moneda)}
          </Typography>
        </Box>

        {/* Facturado en divisa: se imprime la tasa aplicada y el equivalente en
            bolívares, que es lo que permite reconstruir el monto después. */}
        {invoice.moneda && invoice.moneda !== 'VES' && invoice.tasaCambio > 0 && (
          <>
            <Box sx={{...styles.row, ...styles.exentoRow}}>
              <Typography sx={styles.label}>Tasa aplicada:</Typography>
              <Typography sx={styles.value}>
                {formatearMonto(invoice.tasaCambio)} Bs./{invoice.moneda}
              </Typography>
            </Box>
            <Box sx={{...styles.row, ...styles.exentoRow}}>
              <Typography sx={styles.label}>Equivalente:</Typography>
              <Typography sx={styles.value} className="equivalente-value">
                {formatearMoneda(total * invoice.tasaCambio, 'VES')}
              </Typography>
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default InvoiceTotals;